import { Typography } from "@codat/orchard-ui";
import { useDisplayTextQuery } from "api/company";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import { SourceTypeStatus } from "contexts/SourceTypesContext";
import useContinueRedirectUrl from "hooks/useContinueRedirectUrl";
import { useCreateConnectionClickHandler } from "hooks/useCreateConnectionClickHandler";
import useFilteredQueryParams from "hooks/useFilteredQueryParams";
import useFilteredQueryString from "hooks/useFilteredQueryString";
import useNextSourceTypeRedirectHandler from "hooks/useNextSourceTypeRedirectHandler";
import useQueryParams from "hooks/useQueryParams";
import { useSourceTypeDisplayProps } from "hooks/useSourceTypeDisplayProps";
import { useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { fileUploadPlatformKeys, formatUrl, usePageViewedEvent } from "utils";

import Button from "components/Button";
import ConnectionComplete from "components/ConnectionComplete";
import LinkPageRoot from "components/LinkPageRoot";
import SourceTypeSummary from "components/SourceTypeSummary";
import {
  WithSourceTypeEvent,
  WithSourceTypeInnerProps,
} from "components/WithSourceTypeEventContext";
import { CompanySourceTypeParams, ConnectionParams, routes } from "config/urls";

import styles from "./CompanySuccess.module.css";

export type CompanySuccessProps = RouteComponentProps<ConnectionParams> &
  WithSourceTypeInnerProps;

const CompanySuccessContent: React.FC<CompanySuccessProps> = ({
  match,
  sourceTypes,
  sourceTypesStatus,
  currentSourceType,
  nextPrioritySourceType,
}) => {
  const history = useHistory();
  const { state } = useConfig();
  const { redirectUrl: continueRedirectUrl, canContinue } =
    useContinueRedirectUrl(match.params.companyId);
  const filteredQueryParams = useFilteredQueryParams();
  const filteredSearch = useFilteredQueryString(true);
  const [isLoadingCreateConnection, createConnection] =
    useCreateConnectionClickHandler({
      companyId: match.params.companyId,
      platformKey: match.params.platformKey,
      sourceType: currentSourceType?.sourceType,
    });

  const [{ loading: loadingDisplayText, data: displayText }] =
    useDisplayTextQuery({ companyId: match.params.companyId });

  const queryParams = useQueryParams();

  const platformName = queryParams.get("platformName");

  const displayProps = useSourceTypeDisplayProps(currentSourceType);

  const isComplete = useMemo(
    () =>
      sourceTypesStatus === SourceTypeStatus.READY &&
      state.status === ConfigStatusType.READY &&
      !!currentSourceType &&
      !isLoadingCreateConnection &&
      !loadingDisplayText,
    [
      state.status,
      sourceTypesStatus,
      isLoadingCreateConnection,
      currentSourceType,
      loadingDisplayText,
    ]
  );

  usePageViewedEvent({
    ready: !!currentSourceType?.sourceType && isComplete,
    clientConfig: state.data,
    sourceTypes,
  });

  const overviewPageUrl = formatUrl(routes.company.overview, {
    companyId: match.params.companyId,
  });

  const handleAddAnother = () => {
    if (
      currentSourceType?.sourceType === "Banking" &&
      currentSourceType.useCustomIntro
    ) {
      continueRedirectUrl && !state.data.enableFileUpload?.banking
        ? createConnection()
        : history.push(
            formatUrl<CompanySourceTypeParams>(
              routes.company.customIntro,
              {
                companyId: match.params.companyId,
                sourceType: currentSourceType?.sourceType?.toLowerCase(),
              },
              filteredQueryParams
            )
          );
    } else {
      history.push(
        formatUrl<CompanySourceTypeParams>(
          routes.company.link,
          {
            companyId: match.params.companyId,
            sourceType: currentSourceType?.sourceType?.toLowerCase(),
          },
          filteredQueryParams
        )
      );
    }
  };

  const redirectHandler = useNextSourceTypeRedirectHandler(
    match.params.companyId,
    nextPrioritySourceType
  );

  const onNextButtonClick =
    canContinue && nextPrioritySourceType
      ? redirectHandler
      : () => {
          history.push({
            pathname: overviewPageUrl,
            search: filteredSearch,
          });
        };
  const platform =
    displayText?.name || platformName || match.params.platformKey;

  const isFileUpload = displayText?.platformKey
    ? fileUploadPlatformKeys.indexOf(displayText.platformKey) >= 0
    : false;

  const title = isFileUpload ? "Upload successful" : "Connection Successful";

  const subtitle = (() => {
    if (isFileUpload) {
      return `${state.data.clientName} will now be able to view your files`;
    } else if (displayText?.message?.trim()) {
      return displayText.message.trim();
    } else {
      return (
        <>
          You have connected{" "}
          {currentSourceType?.sourceType === "Banking" ? (
            <span data-testid="connection-successful-platform-name">
              your bank account
            </span>
          ) : (
            <span
              data-testid="connection-successful-platform-name"
              className={styles.platformName}
            >
              {platform}
            </span>
          )}
        </>
      );
    }
  })();

  return (
    <LinkPageRoot
      isComplete={isComplete}
      showLinkedAccounts
      data-testid="company-success-page"
      currentSourceType={currentSourceType}
    >
      <ConnectionComplete title={title}>
        <Typography variant="p" as="p" className={styles.successSubtitle}>
          {subtitle}
        </Typography>
        {currentSourceType?.sourceType && (
          <div className={styles.sourceTypeSummaryWrapper}>
            <SourceTypeSummary
              sourceType={currentSourceType.sourceType}
              connectedCount={currentSourceType.connected}
              disconnectedCount={currentSourceType.disconnected}
              uploadedFilesCount={currentSourceType.uploadedFiles}
              onAddAnother={
                displayProps.link.canAddAnother ? handleAddAnother : undefined
              }
            />
          </div>
        )}
        <div className={styles.successFooter}>
          {onNextButtonClick && sourceTypes && (
            <Button
              label="Next"
              analyticsName="Next"
              onClick={onNextButtonClick}
            />
          )}
        </div>
      </ConnectionComplete>
    </LinkPageRoot>
  );
};

export const CompanySuccess: React.FC<RouteComponentProps<ConnectionParams>> = (
  props
) => {
  return (
    <WithSourceTypeEvent
      sourceType={props.match.params?.sourceType}
      eventName={(sourceType) => `Link ${sourceType} success page`}
    >
      {(sourceTypeProps) => (
        <CompanySuccessContent {...sourceTypeProps} {...props} />
      )}
    </WithSourceTypeEvent>
  );
};
